import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { AdminPage, RegistryTable, StatusPill, FeedbackControl } from '@/admin';

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary">{label}</div>
      <div className="mt-1 text-body-sm">{children}</div>
    </div>
  );
}

export function PanelDetailPage() {
  const { panelId = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['panels'], queryFn: () => adminApi.panels.list() });
  const fbPanels = useQuery({ queryKey: ['feedback-summary', 'panels'], queryFn: () => feedbackApi.summary('panels') });
  const fbBio = useQuery({ queryKey: ['feedback-summary', 'biomarkers'], queryFn: () => feedbackApi.summary('biomarkers') });

  const panel = q.data?.panels?.[panelId];
  const biomarkers = panel?.biomarkers ?? [];
  const refreshP = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', 'panels'] });
  const refreshB = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', 'biomarkers'] });

  return (
    <AdminPage
      title={panel?.label || panelId}
      description={panel?.description}
      backTo={{ to: '/admin/panels', label: 'All panels' }}
    >
      {q.isLoading ? <p className="text-body-sm text-text-muted">Loading…</p> : null}
      {q.isError ? <p className="text-body-sm text-flagged">Could not load panels.</p> : null}
      {q.data && !panel ? <p className="text-body-sm text-flagged">No panel “{panelId}”.</p> : null}

      {panel ? (
        <>
          <div className="mb-21 flex items-center gap-8">
            <span className="font-mono text-[12px] text-text-muted">{panelId}</span>
            <FeedbackControl registry="panels" itemId={panelId} itemLabel={panel.label || panelId} openCount={fbPanels.data?.[panelId]?.open ?? 0} onChanged={refreshP} />
          </div>

          <div className="mb-21 grid grid-cols-2 gap-13 rounded border border-border bg-surface p-21 md:grid-cols-4">
            <Meta label="Source type">{panel.source_type || '—'}</Meta>
            <Meta label="Input methods">{(panel.input_methods ?? []).join(', ') || '—'}</Meta>
            <Meta label="Recency">{panel.recency_days ? `${panel.recency_days} days` : '—'}</Meta>
            <Meta label="Completion">{panel.completion_rule?.type || '—'}</Meta>
            <Meta label="Pending action on change">{panel.pending_action_on_change || 'none'}</Meta>
            <Meta label="Used by modes">
              {(panel.used_by ?? []).length ? (
                <div className="flex flex-wrap gap-1">
                  {(panel.used_by ?? []).map((u, i) => (
                    <Link key={`${u.mode_id}-${i}`} to={`/admin/modes/${encodeURIComponent(u.mode_id)}`}
                      className="rounded-pill border border-border px-2 py-0.5 text-[11px] text-accent hover:bg-surface-sunken">
                      {u.mode_id} · {u.criticality}{u.sex_specific ? ` (${u.sex_specific})` : ''}
                    </Link>
                  ))}
                </div>
              ) : '—'}
            </Meta>
          </div>

          <RegistryTable<string>
            columns={[
              { key: 'name', header: 'Biomarker', className: 'font-mono text-[12px]', render: (b) => (
                <Link to={`/admin/biomarkers/${encodeURIComponent(b)}`} className="text-accent hover:underline">{b}</Link>
              ) },
              { key: 'status', header: '', render: () => <StatusPill on label="required" /> },
            ]}
            rows={biomarkers}
            getRowKey={(b) => b}
            count={biomarkers.length}
            state={{ loading: q.isLoading, error: q.isError }}
            emptyText="This panel lists no biomarkers (may accept instruments instead)."
            rowActions={(b) => (
              <FeedbackControl registry="biomarkers" itemId={b} itemLabel={b} openCount={fbBio.data?.[b]?.open ?? 0} onChanged={refreshB} />
            )}
          />
        </>
      ) : null}
    </AdminPage>
  );
}
